import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { generateServiceSchema } from "@/data/serviceSchemaData";
import { Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Breadcrumb } from "@/components/Breadcrumb";
import { Button } from "@/components/ui/button";
import { CTASection } from "@/components/CTASection";
import { RelatedServices } from "@/components/RelatedServices";
import { AccessibilityWidget } from "@/components/AccessibilityWidget";
import { Phone, Droplets, ChevronDown, CheckCircle2 } from "lucide-react";

import heroImage from "@/assets/patio-waterproofing-bergen-county-nj.webp";
import contentImage1 from "@/assets/sealing-paver-patio-nutley-nj.webp";
import contentImage2 from "@/assets/patio-waterproofing-essex-fells-nj-t.webp";

const sealerComparison = [
  {
    system: "Penetrating silane/siloxane",
    bestFor: "Concrete, pavers, bluestone",
    finish: "Invisible",
    notes: "Breathable; does not alter traction",
  },
  {
    system: "Film-forming acrylic/epoxy",
    bestFor: "Covered or interior slabs",
    finish: "Sheen or gloss",
    notes: "Can trap vapor; slippery when wet",
  },
  {
    system: "Liquid-applied membrane",
    bestFor: "Balconies, raised decks",
    finish: "Flexible coating",
    notes: "Best for elevated slabs with drainage concern",
  },
];

const faqs = [
  {
    question: "How long does patio waterproofing last in North Jersey?",
    answer:
      "Most penetrating sealers last 3 to 5 years in North Jersey conditions. Membrane systems can last longer with proper prep, but local freeze-thaw cycles and foot traffic both affect lifespan. High-traffic areas and surfaces exposed to road salt may need reapplication closer to the 3-year mark.",
  },
  {
    question:
      "What surfaces can be waterproofed: concrete, pavers, or stone?",
    answer:
      "All three can be treated, but each requires a different product. Concrete typically takes a penetrating silane/siloxane sealer or a membrane system. Pavers and natural stone need a breathable penetrating sealer that won't alter traction or trap vapor.",
  },
  {
    question:
      "Is it better to waterproof in spring or fall in North Jersey?",
    answer:
      "Late spring is the better window. Surfaces are dry after winter, temperatures are stable, and the coating has the full summer to cure and harden before cold weather returns. Fall works if the job is done before the first frost, but the curing window is shorter.",
  },
  {
    question:
      "Can patio waterproofing fix existing cracks or just prevent new ones?",
    answer:
      "Waterproofing protects sound surfaces, but it doesn't repair cracks. Existing cracks should be filled and allowed to cure before any coating goes down. If you apply a waterproofing product over an unfilled crack, the product bridges the gap but doesn't bond to it.",
  },
  {
    question: "Does patio waterproofing make the surface slippery when wet?",
    answer:
      "Penetrating sealers don't alter surface texture, so traction stays the same. Film-forming sealers can reduce grip when wet. We typically recommend penetrating systems for outdoor patios specifically because traction is not affected.",
  },
  {
    question:
      "How do I know if my North Jersey patio needs re-sealing?",
    answer:
      "The water-drop test gives you an instant read. Pour a small amount of water on the surface. If it beads up and rolls off, the sealer is still working. If it soaks in within a few seconds, or if you're noticing faded color or minor efflorescence returning, it's time to reseal.",
  },
];

export default function PatioWaterproofing() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const scrollToIntro = () => {
    document.getElementById("intro")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <Helmet>
        <title>Patio Waterproofing Totowa, NJ | Masonry Pro Construction</title>
        <meta name="description" content="We waterproof concrete, paver, and stone patios across Bergen, Essex, Passaic, and Morris Counties. Most jobs book within a week. Call Masonry Pro Construction." />
        <meta property="og:description" content="We waterproof concrete, paver, and stone patios across Bergen, Essex, Passaic, and Morris Counties. Most jobs book within a week. Call Masonry Pro Construction." />
        <meta property="og:title" content="Patio Waterproofing Totowa, NJ | Masonry Pro Construction" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqs.map((faq) => ({
              "@type": "Question",
              name: faq.question,
              acceptedAnswer: {
                "@type": "Answer",
                text: faq.answer,
              },
            })),
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(generateServiceSchema({
            name: "Patio Waterproofing",
            description: "Patio waterproofing for concrete, paver, and stone patios across North Jersey.",
            url: "/waterproofing-services/patio-waterproofing",
            image: "https://masonry-pro.com/og/patio-waterproofing.jpg"
          }))}
        </script>
        <meta property="og:image" content="https://masonry-pro.com/og/patio-waterproofing.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:type" content="image/jpeg" />
        <meta name="twitter:image" content="https://masonry-pro.com/og/patio-waterproofing.jpg" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        <Breadcrumb />

        <main>
          {/* Hero Section */}
          <section className="relative overflow-hidden" style={{ backgroundColor: '#0d1017', paddingTop: '160px', paddingBottom: '80px' }}>
            <img src={heroImage} alt="Patio waterproofing services in North Jersey" width={1200} height={800} fetchPriority="high" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(13,16,23,0.45) 0%, rgba(13,16,23,0.65) 100%)' }} />
            <div className="container mx-auto px-4 relative z-10">
              <div className="max-w-3xl">
                <h1 style={{ color: '#f5f5f5', fontSize: '3rem', fontWeight: 'bold', lineHeight: 1.1, marginBottom: '1.5rem', textShadow: '0 2px 8px rgba(0,0,0,0.9)' }}>
                  Patio Waterproofing in Totowa, NJ Serving North Jersey:{" "}
                  <span style={{ color: '#d4a853', WebkitTextFillColor: '#d4a853' }}>
                    Stop Water Damage Before It Starts
                  </span>
                </h1>
                <p style={{ color: 'rgba(225,231,239,0.8)', fontSize: '1.125rem', marginBottom: '2rem', maxWidth: '42rem', textShadow: '0 1px 4px rgba(0,0,0,0.9)' }}>
                  We protect concrete, paver, and stone patios across Bergen, Essex, Passaic, and Morris Counties. Most jobs book within a week.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button variant="hero" size="xl" asChild>
                    <Link to="/contact">Get a Free Estimate</Link>
                  </Button>
                  <Button variant="goldOutline" size="xl" asChild>
                    <a href="tel:+19737252395" className="flex items-center gap-2">
                      <Phone className="h-5 w-5" />
                      Call or Text (973) 725-2395
                    </a>
                  </Button>
                </div>
              </div>
            </div>
            <div style={{ textAlign: 'center', marginTop: '2rem' }}>
              <button
                onClick={scrollToIntro}
                className="text-secondary hover:text-secondary/80 transition-colors animate-bounce"
                aria-label="Scroll down to learn more"
              >
                <ChevronDown className="h-10 w-10" />
              </button>
            </div>
          </section>

          {/* Intro */}
          <section id="intro" className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  What Is the Best Waterproofing{" "}
                  <span className="text-gradient-gold">for a Patio?</span>
                </h2>
                <div className="space-y-4 text-lg text-muted-foreground">
                  <p>
                    North Jersey winters are hard on outdoor surfaces. Freeze-thaw cycles, heavy spring rain, and humid summers break down unprotected concrete, pavers, and stone faster than most homeowners expect. We provide a{" "}
                    <Link to="/waterproofing-services" className="text-secondary hover:underline font-medium">
                      waterproofing service in North Jersey
                    </Link>{" "}
                    for residential patios, balconies, and outdoor living areas throughout Bergen, Essex, Passaic, and Morris Counties.
                  </p>
                  <p>
                    The best method depends on your surface type. Penetrating silane/siloxane sealers work well for porous concrete and pavers, while liquid-applied membranes suit balconies and elevated slabs. According to the American Concrete Institute, sealed concrete can last up to 30% longer than unsealed surfaces.
                  </p>
                </div>

                <div className="mt-8 grid sm:grid-cols-3 gap-4">
                  {[
                    { label: "Penetrating sealers", desc: "Absorb into the surface, invisible finish, best for foot-traffic patios" },
                    { label: "Membrane coatings", desc: "Flexible barrier layer, ideal for balconies and raised decks" },
                    { label: "Surface prep", desc: "Required before either system, since the surface must be clean, dry, and open-pored" },
                  ].map((item) => (
                    <div key={item.label} className="bg-background rounded-xl p-5 border border-border">
                      <h3 className="font-bold mb-2">{item.label}</h3>
                      <p className="text-sm text-muted-foreground">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Warning Signs + Image */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="grid md:grid-cols-2 gap-12 max-w-5xl mx-auto items-center">
                <div>
                  <h2 className="text-3xl md:text-4xl font-bold mb-6">
                    Cracked, Stained, or Pooling Water?{" "}
                    <span className="text-gradient-gold">Act Now</span>
                  </h2>
                  <div className="space-y-4 text-lg text-muted-foreground">
                    <p>
                      If you've noticed surface scaling, white powdery residue (efflorescence), or puddles that linger after rain, those aren't just cosmetic problems. They're early signs that moisture is moving through your surface.
                    </p>
                    <p>
                      North Jersey temperatures can swing between 10°F and 50°F in a single week during winter. That cycle forces water in and out of concrete pores repeatedly, widening cracks a little more each time. Left alone, what starts as surface wear becomes a structural repair.
                    </p>
                    <p>
                      Catching it early is the better move. A waterproofing application on a sound surface costs a fraction of what crack injection or slab replacement runs.
                    </p>
                  </div>
                </div>
                <div className="overflow-hidden rounded-xl border border-border">
                  <img
                    src={contentImage1}
                    alt="Sealing a paver patio with penetrating waterproofing product in Nutley, NJ."
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Sealer Comparison Table */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold mb-4">
                  Penetrating Sealers vs. Membrane Coatings:{" "}
                  <span className="text-gradient-gold">Which Is Right?</span>
                </h2>
                <p className="text-lg text-muted-foreground mb-8">
                  A lot of homeowners search for a waterproofing product online and end up with the wrong one. A film-forming sealer sits on top of the surface and can trap vapor underneath, causing peeling and bubbling. A breathable penetrating sealer absorbs into the pores and lets moisture escape while blocking water from getting in.
                </p>

                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-3 px-4 font-bold text-foreground">System</th>
                        <th className="text-left py-3 px-4 font-bold text-foreground">Best For</th>
                        <th className="text-left py-3 px-4 font-bold text-foreground">Finish</th>
                        <th className="text-left py-3 px-4 font-bold text-foreground">Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sealerComparison.map((row) => (
                        <tr key={row.system} className="border-b border-border/50">
                          <td className="py-3 px-4 font-medium text-foreground">{row.system}</td>
                          <td className="py-3 px-4 text-muted-foreground">{row.bestFor}</td>
                          <td className="py-3 px-4 text-muted-foreground">{row.finish}</td>
                          <td className="py-3 px-4 text-muted-foreground">{row.notes}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </section>

          {/* Surface Prep + Image */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="grid md:grid-cols-2 gap-12 max-w-5xl mx-auto items-center">
                <div className="overflow-hidden rounded-xl border border-border order-2 md:order-1">
                  <img
                    src={contentImage2}
                    alt="Patio waterproofing surface preparation on a residential property in Essex Fells, NJ."
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <div className="order-1 md:order-2">
                  <h2 className="text-3xl md:text-4xl font-bold mb-6">
                    Surface Prep Is What Makes{" "}
                    <span className="text-gradient-gold">Waterproofing Last</span>
                  </h2>
                  <div className="space-y-4 text-lg text-muted-foreground">
                    <p>
                      If a previous waterproofing coat peeled on you, surface prep is almost always the reason. Cleaning, etching if needed, and moisture testing all have to happen before any product goes down. Skip any of those steps and the coating fails early, usually within the first winter.
                    </p>
                    <p>
                      North Jersey properties in tree-heavy areas like Wayne and Paramus accumulate a lot of organic debris: fallen leaves, pollen, algae, and mildew. We clean the surface, let it dry completely, and check moisture levels before we open a product container.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Application + Maintenance */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <div className="grid md:grid-cols-2 gap-12">
                  <div>
                    <h2 className="text-2xl md:text-3xl font-bold mb-4">
                      The Right Application Method{" "}
                      <span className="text-gradient-gold">Matters</span>
                    </h2>
                    <div className="space-y-4 text-muted-foreground">
                      <p>
                        Stamped concrete and bluestone patios have texture depressions that need roller application to work product into the low spots. A flat concrete slab can be sprayed efficiently and evenly.
                      </p>
                      <p>
                        Applying sealer outside the 50°F to 90°F range is one of the most common reasons a waterproofing job fails early. In North Jersey, the practical application window runs from late April through mid-October.
                      </p>
                      <p>
                        We check conditions before every job. If the forecast looks marginal, we reschedule rather than apply in conditions that would compromise the result.
                      </p>
                    </div>
                  </div>

                  <div>
                    <h2 className="text-2xl md:text-3xl font-bold mb-4">
                      Maintenance That{" "}
                      <span className="text-gradient-gold">Extends the Life</span>
                    </h2>
                    <div className="space-y-4 text-muted-foreground">
                      <p>
                        Most penetrating sealers need reapplication every 3 to 5 years. Film-forming coatings may need touch-up sooner if foot traffic is high.
                      </p>
                      <ul className="space-y-2">
                        {[
                          "Annual cleaning and seasonal walkthrough",
                          "Water-drop test to check sealer performance",
                          "Spot treatment for high-traffic areas",
                          "Re-sealing before visible water damage appears",
                        ].map((item) => (
                          <li key={item} className="flex items-start gap-3">
                            <CheckCircle2 className="h-5 w-5 text-secondary mt-0.5 flex-shrink-0" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">
                Frequently Asked Questions
              </h2>

              <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
                {faqs.map((faq) => (
                  <div
                    key={faq.question}
                    className="bg-card rounded-xl p-6 border border-border"
                  >
                    <h3 className="text-lg font-bold mb-2">{faq.question}</h3>
                    <p className="text-muted-foreground">{faq.answer}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <RelatedServices currentCategory="waterproofing" />

          <CTASection />
        </main>

        <Footer />
        <AccessibilityWidget />
      </div>
    </>
  );
}
